import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import {
  addMilliseconds,
  differenceInMilliseconds,
  setSeconds,
} from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import { TimelineAdjustTimeService } from '../../services/timeline-adjust-time.service';
import { MoveShiftDropZoneService } from './move-shift-drop-zone.service';

@Component({
  selector: 'wilson-move-shift-drop-zone-v2',
  imports: [CommonModule],
  template: `<div
    class="drop-zone"
    [style.left.px]="resourcesColumnWidthPx$ | async"
  ></div>`,
  styleUrls: ['./move-shift-drop-zone-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttDateToPositionPxPipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    TimelineAdjustTimeService,
    MoveShiftDropZoneService,
  ],
})
export class MoveShiftDropZoneV2Component
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly moveShiftDropZoneService = inject(MoveShiftDropZoneService);
  private readonly ganttDateToPositionPxPipe = inject(
    GanttDateToPositionPxPipe,
  );

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  ngOnInit(): void {
    this.moveShiftDropZoneService.setShiftWidth();
  }

  protected override async onDragOverHandler(offsetX: number): Promise<void> {
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );

    if (draggingShift) {
      const adjustedOffsetX =
        offsetX - this.moveShiftDropZoneService.shiftWidth / 2;
      const shiftStartDate = setSeconds(
        this.dragAndDropHelperService.calculateTimeFromPx(
          adjustedOffsetX,
          await firstValueFrom(this.dragAndDropModeService.snappingStepSize$),
        ),
        0,
      );
      const timeDifference = differenceInMilliseconds(
        shiftStartDate,
        new Date(draggingShift.startDatetime),
      );
      const draggingMockShift = {
        ...draggingShift,
        startDatetime: addMilliseconds(
          new Date(draggingShift.startDatetime),
          timeDifference,
        ).toISOString(),
        endDatetime: addMilliseconds(
          new Date(draggingShift.endDatetime),
          timeDifference,
        ).toISOString(),
      };

      await this.moveShiftDropZoneService.onDragOverHandler(draggingMockShift);

      const shiftStartDateTime = new Date(draggingMockShift.startDatetime);
      const shiftEndDateTime = new Date(draggingMockShift.endDatetime);
      const currentDemarcatorRange = {
        left: this.ganttDateToPositionPxPipe.transform(shiftStartDateTime, {
          fromRightSide: false,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        right: this.ganttDateToPositionPxPipe.transform(shiftEndDateTime, {
          fromRightSide: true,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        shiftDuration: differenceInMilliseconds(
          shiftEndDateTime,
          shiftStartDateTime,
        ),
        adjustmentNeeded: false,
      };

      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
      this.dragAndDropModeService.setDraggingItemStartDatetime(
        shiftStartDateTime,
      );
      this.dragAndDropModeService.setDraggingItemEndDatetime(shiftEndDateTime);
      this.dragAndDropModeService.setHorizontalDemarcatorRange(
        currentDemarcatorRange,
      );
    }
  }

  protected override async onDragLeaveHandler(): Promise<void> {
    await this.moveShiftDropZoneService.onDragLeaveHandler();
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
  }

  protected override async onDropHandler(): Promise<void> {
    await this.moveShiftDropZoneService.onDropHandler();
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
  }
}
